import _kebabCase from 'lodash/kebabCase';
import { Pixall, Google } from '../analytics/index.js';
import { myWalletClickHelper } from './helpers/index.js';
import { stringifyForDataLayer } from '../analytics/google/helpers/index.js';

/**
 * @summary Sends Google event when search badge is submitted
 * @cmp spec_v
 * @events event1
 */
export function searchSubmitClick(_ref, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref;
  const {
    make,
    model,
    ctaText,
    dealerId,
    ...rest
  } = data;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: `cta::srp::${ctaText || 'search'}`,
    eventNonInteractive: false,
    eventData: {
      vehicle: stringifyForDataLayer({
        catalog: 'atc',
        makeId: _kebabCase(make) || 'any',
        modelId: _kebabCase(model) || 'any'
      })
    }
  }, option);
  Pixall.sendClickEvent({
    hasListingType: false,
    eventSource: 'button',
    label: `cta::srp::${ctaText || 'search'}`,
    listingCode: dealerId,
    make,
    model
  }, dataLayer, option);
  return {
    make,
    model,
    rest
  };
}

/**
 * @summary Sends Google event when zipcode changes
 * @events event1
 */
export function zipOnChange(_ref2, option) {
  let {
    data = {}
  } = _ref2;
  const {
    zipcode = '',
    ...rest
  } = data;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: `zip-code::${zipcode}`,
    eventNonInteractive: false
  }, option);
  return rest;
}

/**
 * @summary Sends BIRF event vehicle make selection is clicked
 * @cmp make
 * @events event1
 */
export async function browsByMakeClick(_ref3, option) {
  let {
    data = {}
  } = _ref3;
  // eslint-disable-next-line camelcase
  const {
    co_txt_url = '',
    makeSelection,
    pageType = null
  } = data;
  Google.sendClickEvent({
    eventElement: 'search-car',
    // eslint-disable-next-line camelcase
    eventResult: makeSelection ? `text-link::srp::${co_txt_url.toLowerCase()}` : 'text-link::browse-by-make',
    eventNonInteractive: pageType !== 'homepage'
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall event when vehicle make selection is clicked
 * @cmp make
 * @events event1
 */
export async function browsAllMakesClick(_ref4, option) {
  let {
    data = {},
    dataLayer
  } = _ref4;
  // eslint-disable-next-line camelcase
  const {
    co_txt_url = '',
    makeSelection,
    pageType = null
  } = data;

  // eslint-disable-next-line camelcase
  const label = makeSelection ? `text-link::srp::${co_txt_url.toLowerCase()}` : 'text-link::browse-all-make';
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: label,
    eventNonInteractive: pageType !== 'homepage'
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'link',
    label
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends Google and Pixall event when browse by budget is clicked
 * @cmp brw_bdgt
 * @events event1
 */
export const advancedSearchClick = function (_temp, option) {
  let {
    event,
    data,
    dataLayer,
    state
  } = _temp === void 0 ? {} : _temp;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventNonInteractive: false,
    eventResult: 'text-link::advance-search',
    ...data
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'link',
    label: 'text-link::advance-search',
    hasListingType: false
  }, dataLayer, option);
  return {};
};

/**
 * @summary Sends Google and Pixall event when browse by budget is clicked
 * @cmp brw_bdgt
 * @events event1
 */
export const browsByBudgetClick = function (_temp2, option) {
  let {
    event,
    data,
    dataLayer,
    state
  } = _temp2 === void 0 ? {} : _temp2;
  const {
    pageType = ''
  } = data;
  const isFord = state?.brand === 'ford';
  const result = isFord ? 'text-link::browse-by-budget' : 'text-link::open::browse-by-budget';
  myWalletClickHelper({
    eventResult: result
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'userAccount',
    feature: 'MyWallet',
    label: pageType === 'homepage' ? 'cta::open::find-cars-by-budget' : 'brw_bdgt'
  }, dataLayer, option);
  return {};
};

/**
 * @summary Sends Google event vehicle style selection is clicked
 * @cmp sty | sty_btm
 * @events event1
 */
export async function browsByStyleClick(_ref5, option) {
  let {
    data = {},
    event,
    dataLayer,
    state
  } = _ref5;
  // eslint-disable-next-line camelcase
  const {
    styleSelection,
    co_txt_url = '',
    pageType = null,
    firePixall,
    label = 'srch_sty',
    isHP = false,
    dealerId
  } = data;
  const isFord = state?.brand === 'ford';
  const result = isFord ? 'text-link::shop-vehicles-by-style' : 'text-link::browse-by-style';
  const pixallLabel = isHP ? result : label;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventNonInteractive: pageType !== 'homepage',
    // eslint-disable-next-line camelcase
    eventResult: styleSelection ? `tile::srp::${co_txt_url.toLowerCase()}` : result,
    ...data
  }, option);
  if (firePixall) {
    Pixall.sendClickEvent({
      hasListingType: false,
      eventSource: isFord || isHP ? 'link' : 'userAccount',
      // eslint-disable-next-line camelcase
      label: styleSelection ? `tile::srp::${co_txt_url.toLowerCase()}` : pixallLabel,
      listingCode: dealerId,
      ...data
    }, dataLayer, option);
  }
  return {};
}

/**
 * @summary Sends Google event make drop-down selection is made
 * @cmp selbx_make_hp
 * @events event1
 */
export const makeDropDownClick = function (_temp3, option) {
  let {
    data
  } = _temp3 === void 0 ? {} : _temp3;
  const {
    make,
    pageType = null,
    eventNonInteractive = null,
    ...rest
  } = data;
  let makeName;
  if (make) {
    makeName = _kebabCase(make);
  }
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `dropdown::make::${makeName}`,
    eventNonInteractive: eventNonInteractive !== null ? eventNonInteractive : pageType !== 'homepage'
  }, option);
  return {
    make,
    rest
  };
};

/**
 * @summary Sends Google event model drop-down selection is made
 * @cmp selbx_model_hp
 * @events event1
 */
export const modelDropDownClick = function (_temp4, option) {
  let {
    data
  } = _temp4 === void 0 ? {} : _temp4;
  const {
    model,
    pageType = null,
    eventNonInteractive = null,
    ...rest
  } = data;
  let modelName;
  if (model) {
    modelName = _kebabCase(model);
  }
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `dropdown::model::${modelName}`,
    eventNonInteractive: eventNonInteractive !== null ? eventNonInteractive : pageType !== 'homepage'
  }, option);
  return {
    model,
    rest
  };
};

/**
 * @summary Sends pixall event make help/example link is clicked
 * @cmp ad_make_lnk
 * @events event1
 */
export async function makeExampleLinkClick(_temp5, option) {
  let {
    event,
    data = {},
    dataLayer
  } = _temp5 === void 0 ? {} : _temp5;
  const cmp = 'ad_make_lnk';
  const {
    pixallData,
    makeName
  } = data;
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: `cta::hero-ad::${makeName}`,
    eventNonInteractive: false
  }, option);
  Pixall.sendClickEvent({
    label: cmp,
    eventSource: 'oem',
    feature: 'OEM Native Links',
    ...pixallData
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends pixall event make help/example link is clicked
 * @cmp ad_mdl_lnk
 * @events event1
 */
export async function modelExampleLinkClick(_temp6, option) {
  let {
    event,
    data = {},
    dataLayer
  } = _temp6 === void 0 ? {} : _temp6;
  const cmp = 'ad_mdl_lnk';
  const {
    pixallData,
    modelName
  } = data;
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: `cta::hero-ad::${modelName}`,
    eventNonInteractive: false
  }, option);
  Pixall.sendClickEvent({
    label: cmp,
    eventSource: 'oem',
    feature: 'OEM Native Links',
    ...pixallData
  }, dataLayer, option);
  return {
    cmp
  };
}
export const searchAllVehiclesClick = (_ref6, option) => {
  let {
    dataLayer = {}
  } = _ref6;
  const label = 'cta::srp::search-all-vehicles';
  Pixall.sendClickEvent({
    dealerCode: 'atfordcpo',
    eventSource: 'link',
    eventType: 'click',
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    label
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: label
  }, option);
  return {
    label
  };
};
export const findByBudgetClick = (_ref7, option) => {
  let {
    dataLayer = {}
  } = _ref7;
  const label = 'brw_bdgt';
  Pixall.sendClickEvent({
    dealerCode: 'atfordcpo',
    eventSource: 'ueseAccount',
    eventType: 'click',
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    feature: 'MyWallet',
    label
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: 'text-link::browse-by-budget'
  }, option);
  return {
    label
  };
};
export const searchBySUVsClick = (_ref8, option) => {
  let {
    dataLayer = {}
  } = _ref8;
  const label = 'cta::srp::suvs';
  Pixall.sendClickEvent({
    dealerCode: 'atfordcpo',
    eventSource: 'link',
    eventType: 'click',
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    label
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: label
  }, option);
  return {
    label
  };
};
export const searchByTrucksClick = (_ref9, option) => {
  let {
    dataLayer = {}
  } = _ref9;
  const label = 'cta::srp::trucks';
  Pixall.sendClickEvent({
    dealerCode: 'atfordcpo',
    eventSource: 'link',
    eventType: 'click',
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    label
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: label
  }, option);
  return {
    label
  };
};
export const zipSubmitClick = (_ref10, option) => {
  let {
    data = {},
    dataLayer = {}
  } = _ref10;
  const {
    zip = ''
  } = data;
  const label = `zip::${zip}`;
  Pixall.sendClickEvent({
    dealerCode: 'atfordcpo',
    eventSource: 'link',
    eventType: 'click',
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    label
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: label
  }, option);
  return {
    label
  };
};
export default {
  searchSubmitClick,
  browsByMakeClick,
  browsAllMakesClick,
  browsByStyleClick,
  makeDropDownClick,
  modelDropDownClick,
  advancedSearchClick,
  browsByBudgetClick,
  makeExampleLinkClick,
  modelExampleLinkClick,
  zipOnChange,
  searchAllVehiclesClick,
  findByBudgetClick,
  searchBySUVsClick,
  searchByTrucksClick,
  zipSubmitClick
};